import { create } from "zustand";
import { Intercom, show } from "@intercom/messenger-js-sdk";
import { getActiveIntercomAppId } from "@/constants/intercom";
import { useAuthStore } from "@utils/stores/authStore";
import { Endpoints } from "@utils/api/endpoints";
import axios from "axios";
import { AXIOS_CONFIG } from "@utils/api";
import { backend_url } from "@/constants";

/**
 * Fetch Intercom JWT token from the backend.
 *
 * @returns Promise<string | null> - JWT token or null if fetch failed
 */
const fetchIntercomJWT = async (): Promise<string | null> => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.onboarding.intercomJwt}`,
      AXIOS_CONFIG
    );
    console.log("[Intercom] JWT response:", {
      status: response.status,
      data: response.data,
      content: response.data?.content,
      intercomJwt: response.data?.content?.intercomJwt,
      intercom_jwt: response.data?.content?.intercom_jwt, // Also check snake_case for debugging
    });
    // Backend converts snake_case to camelCase via return_response, so we need intercomJwt
    const jwt = response.data.content?.intercomJwt || null;
    if (!jwt) {
      console.warn("[Intercom] JWT token not found in response", response.data);
    }
    return jwt;
  } catch (error) {
    console.error("[Intercom] Failed to fetch JWT:", error);
    if (axios.isAxiosError(error)) {
      console.error("[Intercom] JWT fetch error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
    }
    return null;
  }
};

export interface IntercomStore {
  /**
   * Whether Intercom has been successfully initialized
   */
  isInitialized: boolean;

  /**
   * Whether Intercom is currently initializing
   */
  isInitializing: boolean;

  /**
   * Initialize Intercom with user data of the currently logged in user.
   * @returns Promise<boolean> - True if initialization was successful, false otherwise
   */
  initialize: () => Promise<boolean>;

  /**
   * Show the Intercom messenger widget.
   * Only shows if Intercom is initialized.
   * @returns boolean - True if shown successfully, false if not initialized
   */
  showIntercom: () => boolean;

  /**
   * Start a survey by survey ID.
   * Only works if Intercom is initialized.
   * @param surveyId - The survey ID to start
   * @returns boolean - True if survey was started successfully, false otherwise
   */
  startSurvey: (surveyId: number | string) => boolean;

  /**
   * Track custom events in Intercom.
   * Only works if Intercom is initialized.
   * @param eventName - The name of the event to track
   * @param metadata - Optional metadata to include with the event
   * @returns boolean - True if event was tracked successfully, false otherwise
   */
  trackEvent: (eventName: string, metadata?: Record<string, any>) => boolean;

  /**
   * Reset the initialization state (useful for logout)
   */
  reset: () => void;
}

export const useIntercomStore = create<IntercomStore>((set, get) => ({
  isInitialized: false,
  isInitializing: false,

  initialize: async () => {
    const state = get();

    // Prevent multiple simultaneous initializations
    if (state.isInitializing) {
      console.log("[Intercom] Already initializing, skipping...");
      return state.isInitialized;
    }

    // If already initialized, return success
    if (state.isInitialized) {
      console.log("[Intercom] Already initialized");
      return true;
    }

    set({ isInitializing: true });

    try {
      const { user, isTeamMember, role } = useAuthStore.getState();

      console.log("[Intercom] Initializing...");
      if (user) {
        console.log("[Intercom] User found!");

        // Fetch Intercom JWT token for secure authentication
        const intercomJwt = await fetchIntercomJWT();

        // Use getActiveIntercomAppId() to match the JWT workspace (TEST in dev, PRODUCTION in prod)
        const activeAppId = getActiveIntercomAppId();
        console.log("[Intercom] Using app ID:", activeAppId);

        // Hide Intercom widget for taxpayers (clients)
        const isClient = role === "client" || user.role === "client";

        const intercomConfig: any = {
          app_id: activeAppId,
          user_id: user?.id,
          name: user.firstName + " " + user.lastName,
          email: user.email,
          hide_default_launcher: isClient,
          // Custom
          customer_role: isTeamMember() ? "Team Member" : "Owner",
        };

        // Add JWT token if available (using intercom_user_jwt for JWT-based authentication)
        if (intercomJwt) {
          intercomConfig.intercom_user_jwt = intercomJwt;
          console.log("[Intercom] JWT token added for secure authentication");
        } else {
          console.warn(
            "[Intercom] JWT token not available, continuing without it"
          );
        }

        Intercom(intercomConfig);
      } else {
        console.log("[Intercom] No user initialized.");
        const activeAppId = getActiveIntercomAppId();
        // For non-authenticated users, show Intercom widget
        Intercom({
          app_id: activeAppId,
          hide_default_launcher: false,
        });
      }

      console.log("[Intercom] Initialization complete.");
      set({ isInitialized: true, isInitializing: false });
      return true;
    } catch (error) {
      console.error("Error initializing Intercom:", error);
      set({ isInitialized: false, isInitializing: false });
      return false;
    }
  },

  showIntercom: () => {
    const { isInitialized } = get();

    if (!isInitialized) {
      console.warn("[Intercom] Cannot show - Intercom not initialized");
      return false;
    }

    if (typeof window !== "undefined" && window.Intercom) {
      try {
        show();
        return true;
      } catch (error) {
        console.error("[Intercom] Error showing messenger:", error);
        return false;
      }
    }

    console.warn("[Intercom] window.Intercom not available");
    return false;
  },

  startSurvey: (surveyId: number | string) => {
    const { isInitialized } = get();

    if (!isInitialized) {
      console.warn("[Intercom] Cannot start survey - Intercom not initialized");
      return false;
    }

    if (typeof window !== "undefined" && window.Intercom) {
      try {
        window.Intercom("startSurvey", surveyId);
        return true;
      } catch (error) {
        console.error("[Intercom] Error starting survey:", error);
        return false;
      }
    }

    console.warn("[Intercom] window.Intercom not available");
    return false;
  },

  trackEvent: (eventName: string, metadata?: Record<string, any>) => {
    const { isInitialized } = get();

    if (!isInitialized) {
      console.warn("[Intercom] Cannot track event - Intercom not initialized");
      return false;
    }

    if (typeof window !== "undefined" && window.Intercom) {
      try {
        window.Intercom("trackEvent", eventName, metadata);
        return true;
      } catch (error) {
        console.error("[Intercom] Error tracking event:", error);
        return false;
      }
    }

    console.warn("[Intercom] window.Intercom not available");
    return false;
  },

  reset: () => {
    set({ isInitialized: false, isInitializing: false });
  },
}));
